import { useState } from "react";
import { CheckCircle2, ChevronDown, Flag, Lightbulb, Target } from "lucide-react";

import { SectionLabel } from "./section-label";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import type { WeekEntry } from "@/routes/_app/-timeline-v3.data";

interface WeekDetailCardProps {
  entry: WeekEntry;
}

type WeekTone = "discovery" | "build" | "select";

function weekTone(number: 1 | 2 | 3 | 4 | 5 | 6): WeekTone {
  if (number <= 2) return "discovery";
  if (number <= 4) return "build";
  return "select";
}

export function WeekDetailCard({ entry }: WeekDetailCardProps) {
  const tone: WeekTone = weekTone(entry.number);
  const [open, setOpen] = useState(false);
  // Reset to collapsed when switching weeks.
  // The key is bumped by parent re-render on entry change — local state resets naturally
  // when React remounts the component. We don't force a remount here; if user wants every
  // week to start collapsed they re-open each one.
  return (
    <article data-slot="week-detail" className="glow-card-static p-6">
      <CardHeader entry={entry} tone={tone} open={open} onToggle={() => setOpen((v) => !v)} />
      {open ? <CardBody entry={entry} /> : null}
    </article>
  );
}

function CardHeader({
  entry,
  tone,
  open,
  onToggle,
}: {
  entry: WeekEntry;
  tone: WeekTone;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <header
      className={cn(
        "flex flex-col gap-1 border-b border-[oklch(0.91_0.008_240_/_0.6)]",
        open ? "pb-4" : "border-b-0 pb-0"
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="hover:bg-muted/30 -mx-2 flex w-[calc(100%+1rem)] items-start justify-between gap-3 rounded-md px-2 py-1 text-left transition-colors"
      >
        <div className="flex flex-col gap-1">
          <HeaderEyebrow number={entry.number} window={entry.window} tone={tone} />
          <h2 className="text-lg font-medium tracking-tight">{entry.title}</h2>
        </div>
        <ChevronDown
          className="text-foreground/50 mt-2 size-4 shrink-0 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
    </header>
  );
}

const EYEBROW_STYLE: Record<WeekTone, string> = {
  discovery: "bg-[oklch(0.55_0.17_190_/_0.1)] text-[oklch(0.45_0.18_195)]",
  build: "bg-[oklch(0.74_0.16_80_/_0.1)] text-[oklch(0.5_0.18_60)]",
  select: "bg-[oklch(0.62_0.18_280_/_0.1)] text-[oklch(0.4_0.2_280)]",
};

function HeaderEyebrow({
  number,
  window,
  tone,
}: {
  number: 1 | 2 | 3 | 4 | 5 | 6;
  window: string;
  tone: WeekTone;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          "font-data inline-flex h-5 items-center rounded px-1.5 text-[10px] font-semibold tracking-wide",
          EYEBROW_STYLE[tone]
        )}
      >
        Week {number}
      </span>
      <span className="font-data text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
        {window}
      </span>
    </div>
  );
}

function CardBody({ entry }: { entry: WeekEntry }) {
  return (
    <div className="mt-5 flex flex-col gap-5">
      <SectionLabel label="Focus" icon={<Target className="h-3 w-3" />}>
        <p>{entry.focus}</p>
      </SectionLabel>
      <SectionLabel label="Gate" icon={<Flag className="h-3 w-3" />}>
        <p>{entry.gate}</p>
      </SectionLabel>
      <SectionLabel label="Deliverables" icon={<CheckCircle2 className="h-3 w-3" />}>
        <DeliverableList items={entry.deliverables} />
      </SectionLabel>
      {entry.conclusions ? (
        <SectionLabel label="Conclusions" icon={<Lightbulb className="h-3 w-3" />} tone="accent">
          <ConclusionList items={entry.conclusions} />
        </SectionLabel>
      ) : null}
    </div>
  );
}

function DeliverableList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item) => (
        <DeliverableRow key={item} text={item} />
      ))}
    </ul>
  );
}

function DeliverableRow({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="mt-[3px] h-3.5 w-3.5 shrink-0 text-[oklch(0.66_0.16_155)]" />
      <span>{text}</span>
    </li>
  );
}

function ConclusionList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item) => (
        <ConclusionRow key={item} text={item} />
      ))}
    </ul>
  );
}

function ConclusionRow({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-[8px] inline-block h-1 w-1 shrink-0 rounded-full bg-[oklch(0.55_0.17_190)]" />
      <span>{text}</span>
    </li>
  );
}
